import { getApiBaseUrl } from "../utils/ssoProvider";
import axios from "axios";
import { setInBothStorages, getStoredValue, safeSessionStorage, safeLocalStorage } from "./logics";
import store from "../redux/store";
import { setSessionExpired } from "../redux/sessionSlice";
import { getSSOConfig } from "../utils/ssoProvider";

const API_URL = getApiBaseUrl();

export const googleLogin = async (code, redirectUri) => {
  // Must match the redirect_uri sent when starting the flow (GoogleSignInButton)
  // - Google's token exchange rejects a mismatch.
  let callbackUrl = `${API_URL}/v1/auth/google/callback?code=${code}`;
  if (redirectUri) {
    callbackUrl += `&redirect_uri=${encodeURIComponent(redirectUri)}`;
  }
  let res = await axios.get(callbackUrl, {
    withCredentials: true,
  });

  setInBothStorages("email", res.data.email);
  setInBothStorages("token", res.data.token);
  setInBothStorages("expiry_in", res.data.expires_in);
  setInBothStorages("is_super_admin", res.data.is_super_admin);
  setInBothStorages("token_issued_at", new Date().toISOString());
  setInBothStorages("session_expires_at", res.data.session_expires_at || "");
  // Route guards (ProtectedAdminRoutes, PrivateLayout) check isLoggedIn, not just
  // token presence - without this, a stale token surviving a previous logout's
  // storage.clear() (a cross-tab race, a bookmarked admin URL, a replayed OAuth
  // callback) can let the guard through on token alone while isLoggedIn is still
  // "false" from the last logout.
  setInBothStorages("isLoggedIn", "true");
  markSessionStarted();
};

// A successful login means the session is alive. Login lands via an in-app
// navigate("/") (same page load), so a session-expired flag left from before
// login would otherwise survive and axiosInstance would silently refuse every
// request - the app then shows "No internet connection" until a reload.
const markSessionStarted = () => store.dispatch(setSessionExpired(false));

// Handles both the Service Points and SP-lite widget callbacks - which backend
// endpoint gets called is looked up from domainConfig.json (via ssoProvider.js)
// based on the current hostname, not branched on here.
// NOTE: the SP-lite endpoint (/v1/auth/sp-lite/callback) does not exist on the
// backend yet - that call will 404 until it's implemented.
export const ssoLogin = async token => {
  const { loginEndpoint } = getSSOConfig();
  const res = await axios.post(
    `${API_URL}${loginEndpoint}`,
    { token },
    {
      withCredentials: true,
    }
  );

  setInBothStorages("email", res.data.email);
  setInBothStorages("token", res.data.token);
  setInBothStorages("expiry_in", res.data.expires_in);
  setInBothStorages("is_super_admin", res.data.is_super_admin);
  setInBothStorages("token_issued_at", new Date().toISOString());
  setInBothStorages("session_expires_at", res.data.session_expires_at || "");
  // Same reasoning as googleLogin above: route guards check isLoggedIn, not just
  // token presence.
  setInBothStorages("isLoggedIn", "true");
  markSessionStarted();
};

// transient: true means "couldn't tell" (aborted/offline/5xx) - callers must not treat it as a dead session.
const refreshForAuthCheck = async () => {
  try {
    return (await getRefreshToken()) ? null : { success: false };
  } catch (_) {
    return { success: false, transient: true };
  }
};

export const isAuthenticated = async () => {
  // Check if the token is expired before making the request
  if (isTokenExpired()) {
    const failure = await refreshForAuthCheck();
    if (failure) return failure;
  }

  const token = getStoredValue("token");
  try {
    const res = await axios.get(`${API_URL}/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    const data = res.data;

    // Set values in both storages
    setInBothStorages("profile", data.user.profile_image);
    setInBothStorages("firstname", data.user.first_name);
    setInBothStorages("lastname", data.user.last_name);
    setInBothStorages("role", data.role);
    setInBothStorages("storeid", data?.store?.id);
    setInBothStorages("documentUploaded", data.store?.is_document_uploaded);
    setInBothStorages("emailaccount", data.store?.is_email_account_added);
    setInBothStorages("shopifyaccount", data.store?.is_shopify_account_added);
    setInBothStorages("isonboarded", data.store?.is_onboarded);
    setInBothStorages("preferedlanguage", data.user?.prefered_language);
    setInBothStorages("storename", data?.store?.store_allias_name || data?.store?.name);
    setInBothStorages("ownername", data?.store?.client?.name);
    setInBothStorages("storedata", data?.store?.store_id);
    setInBothStorages("signed-on", data?.user?.created_at);
    setInBothStorages("id", data.user.id);
    setInBothStorages("isLoggedIn", "true");
    setInBothStorages(
      "is_super_admin",
      String(data.user.is_super_admin).replace(/^./, c => c.toUpperCase())
    );

    // Return both success and data
    return {
      success: true,
      user: data.user,
      store: data.store,
      role: data.role,
    };
  } catch (error) {
    // A token problem comes back as a 4xx; anything else (aborted by a reload, offline, 5xx) is
    // not a reason to refresh, let alone to end the session.
    if (!isServerRejection(error)) return { success: false, transient: true };
    const failure = await refreshForAuthCheck();
    if (failure) return failure;

    // Retry the request with the new token
    try {
      const newToken = getStoredValue("token");
      const retryRes = await axios.get(`${API_URL}/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${newToken}`,
        },
        withCredentials: true,
      });

      const data = retryRes.data;

      // Set values in both storages
      setInBothStorages("profile", data.user.profile_image);
      setInBothStorages("firstname", data.user.first_name);
      setInBothStorages("lastname", data.user.last_name);
      setInBothStorages("role", data.role);
      setInBothStorages("storeid", data?.store?.id);
      setInBothStorages("documentUploaded", data.store?.is_document_uploaded);
      setInBothStorages("emailaccount", data.store?.is_email_account_added);
      setInBothStorages("shopifyaccount", data.store?.is_shopify_account_added);
      setInBothStorages("isonboarded", data.store?.is_onboarded);
      setInBothStorages("preferedlanguage", data.user?.prefered_language);
      setInBothStorages("storename", data?.store?.store_allias_name || data?.store?.name);
      setInBothStorages("ownername", data?.store?.client?.name);
      setInBothStorages("storedata", data?.store?.store_id);
      setInBothStorages("signed-on", data?.user?.created_at);
      setInBothStorages("id", data.user.id);
      setInBothStorages("isLoggedIn", "true");
      setInBothStorages(
        "is_super_admin",
        String(data.user.is_super_admin).replace(/^./, c => c.toUpperCase())
      );

      return {
        success: true,
        user: data.user,
        store: data.store,
        role: data.role,
      };
    } catch (retryError) {
      return isServerRejection(retryError) ? { success: false } : { success: false, transient: true };
    }
  }
};

const refreshTokenRequest = async () => {
  const isLoggedIn = getStoredValue("isLoggedIn");
  const wasLoggedIn = isLoggedIn === "true";
  const isSuperAdmin = getStoredValue("is_super_admin");
  const sessionStoreid = safeSessionStorage.getItem("storeid");
  const localStoreid = safeLocalStorage.getItem("storeid");
  if (isLoggedIn === "false") {
    const currentPath = window.location.pathname;
    if (!currentPath.includes("/signin") && !currentPath.includes("/admin-signin")) {
      // If user is a super admin, redirect to admin signin, otherwise to regular signin
      if (isSuperAdmin?.toLowerCase() === "true" && sessionStoreid === "undefined") {
        window.location.href = "/admin-signin";
      } else {
        window.location.href = "/signin";
      }
    }
    return;
  }

  // A storeless user has "storeid" written as the literal string "undefined"
  // (setInBothStorages("storeid", data?.store?.id) stringifies a JS undefined),
  // not an absent key - so it must be screened out here exactly like "null" is,
  // or it gets sent to the backend as store_id=undefined and mismatches the
  // "{store_id}_refresh_token" cookie the storeless login actually set (which
  // uses Python None, i.e. cookie key "None_refresh_token").
  const isRealStoreId = value => value && value !== "null" && value !== "undefined";

  let storeid = null;
  if (isSuperAdmin?.toLowerCase() === "true" && !isRealStoreId(sessionStoreid)) {
    storeid = null;
  } else if (isRealStoreId(sessionStoreid)) {
    storeid = sessionStoreid;
  } else if (isRealStoreId(localStoreid)) {
    storeid = localStoreid;
  } else {
    storeid = null;
  }

  try {
    const res = await axios.post(
      `${API_URL}/v1/auth/token/refresh`,
      {},
      {
        withCredentials: true,
        params: {
          store_id: storeid,
        },
      }
    );
    // Update tokens for all user types using setInBothStorages
    setInBothStorages("email", res.data.email);
    setInBothStorages("token", res.data.token);
    setInBothStorages("expiry_in", res.data.expires_in);
    setInBothStorages("is_super_admin", res.data.is_super_admin);
    setInBothStorages("token_issued_at", new Date().toISOString());
    setInBothStorages("session_expires_at", res.data.session_expires_at || "");
    return true;
  } catch (err) {
    // Only a 4xx is the server saying the refresh token is dead. No response (request aborted by a
    // reload/navigation, offline, network blip) or a 5xx says nothing about the session - expiring
    // on those made SessionExpiredGate wipe storage and log out a perfectly valid session.
    if (!isServerRejection(err)) throw err;
    // Hand over to the session-expired screen (SessionExpiredGate) instead of a toast/redirect -
    // but only if there was a session to expire when this refresh was sent. A first-time visitor on
    // /signin gets a 401 too, and that answer can land after they've already logged in.
    if (wasLoggedIn) store.dispatch(setSessionExpired(true));
    return false;
  }
};

export const isServerRejection = err => {
  const status = err?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

// Resolves true (refreshed) or false (session dead, already marked expired); rejects with the
// original error when the refresh could not reach a verdict (see isServerRejection).
// One refresh at a time. A page load fires 3-5 auth checks at once (layout, navbar, page, and any
// request that 401s), and each used to POST /auth/token/refresh on its own - every one of them a
// separate ServicePoints call. Concurrent callers now share the in-flight request and its result.
let refreshInFlight = null;

export const getRefreshToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshTokenRequest().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const isTokenExpired = () => {
  const expiryIn = getStoredValue("expiry_in");
  const issuedAt = getStoredValue("token_issued_at");
  const currentTime = new Date();

  // Handle missing expiry
  if (!expiryIn) {
    return true;
  }

  // If issuedAt is missing, treat token as expired (don't create a false issuedAt)
  if (!issuedAt) {
    return true;
  }

  const expiryMinutes = parseInt(expiryIn, 10);
  if (isNaN(expiryMinutes)) {
    return true;
  }

  const issuedDate = new Date(issuedAt);
  if (isNaN(issuedDate.getTime())) {
    return true;
  }

  const expiryTime = new Date(issuedDate.getTime() + expiryMinutes * 60 * 1000);
  const expired = currentTime > expiryTime;

  return expired;
};

export const EmailSignup = async (email, password, confirm_password, first_name, last_name) => {
  // Create FormData instance
  const formData = new FormData();
  formData.append("first_name", first_name);
  formData.append("last_name", last_name);
  formData.append("email", email);
  formData.append("password", password);
  formData.append("confirm_password", confirm_password);

  // Send POST request with form-data
  const response = await axios.post(`${API_URL}/v1/auth/signup`, formData, {
    headers: {
      "Content-Type": "multipart/form-data", // Important for form-data
    },
  });

  return response.data; // Return the server response
};

export const EmailSignin = async (email, password) => {
  // Create a FormData object
  const formData = new FormData();
  formData.append("email", email);
  formData.append("password", password);

  // Make the API request
  const res = await axios.post(`${API_URL}/v1/auth/login`, formData, {
    headers: {
      "Content-Type": "multipart/form-data", // Ensures the request is sent as form-data
    },
  });

  // // Save data in both storages
  if (res?.data?.is_verified !== "False") {
    setInBothStorages("email", res?.data?.email);
    setInBothStorages("token", res?.data?.token);
    setInBothStorages("expiry_in", res?.data?.expires_in);
    setInBothStorages("is_super_admin", res?.data?.is_super_admin);
    setInBothStorages("token_issued_at", new Date().toISOString());
    setInBothStorages("session_expires_at", res?.data?.session_expires_at || "");
    markSessionStarted();
  }
  return res.data; // Return the response data for further processing
};

export const verifyEmailAPI = async (email, verification_code) => {
  // Create a FormData object
  const formData = new FormData();
  formData.append("email", email);
  formData.append("verification_code", verification_code);

  // Make the API request
  const response = await axios.post(`${API_URL}/v1/auth/verify-email`, formData, {
    headers: {
      "Content-Type": "multipart/form-data", // Ensures form-data is sent correctly
    },
  });

  return response.data;
};
